// ═══════════════════════════════════════════════════════════════
// MÓDULO: Otras Deducciones
// Gestiona deducciones como seguros, sindicatos, embargos, etc.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  MinusCircle,
  Plus,
  Search,
  Download,
  Edit2,
  Trash2,
  Shield,
  AlertCircle,
  CheckCircle,
  Calendar,
  DollarSign
} from 'lucide-react';
import { useCompany } from '../../../contexts/CompanyContext';
import useEmployees from '../../../hooks/useEmployees';
import OtherDeductionModal from './OtherDeductionModal';
import {
  getOtherDeductions,
  deleteOtherDeduction,
  getDeductionsSummary
} from '../../../api/otherDeductions';
import Pagination from '../../common/Pagination';

const PAGE_SIZE = 15;

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

const deductionTypes = [
  { value: 'seguro_salud',        label: 'Seguro de Salud',         cls: 'bg-blue-100 text-blue-700'   },
  { value: 'seguro_vida',         label: 'Seguro de Vida',          cls: 'bg-blue-100 text-blue-700'   },
  { value: 'cuota_sindical',      label: 'Cuota Sindical',          cls: 'bg-gray-100 text-gray-700'   },
  { value: 'embargo',             label: 'Embargo Judicial',        cls: 'bg-amber-100 text-amber-700' },
  { value: 'pension_alimenticia', label: 'Pensión Alimenticia',     cls: 'bg-amber-100 text-amber-700' },
  { value: 'cooperativa',         label: 'Cooperativa',             cls: 'bg-green-100 text-green-700' },
  { value: 'otros',               label: 'Otros',                   cls: 'bg-gray-100 text-gray-600'   },
];

function StatCard({ icon: Icon, iconCls, bgCls, label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`w-10 h-10 ${bgCls} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconCls}`} />
        </div>
      </div>
    </div>
  );
}

export default function OtherDeductionsModule() {
  const { currentCompany } = useCompany();
  const { employees } = useEmployees();

  const [deductions, setDeductions]             = useState([]);
  const [filteredDeductions, setFilteredDeductions] = useState([]);
  const [summary, setSummary]                   = useState(null);
  const [loading, setLoading]                   = useState(true);

  const [searchTerm, setSearchTerm]             = useState('');
  const [selectedType, setSelectedType]         = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [showRecurrentOnly, setShowRecurrentOnly] = useState(false);

  const [showModal, setShowModal]               = useState(false);
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [page, setPage]                         = useState(1);

  useEffect(() => {
    if (currentCompany) {
      loadDeductions();
    }
  }, [currentCompany]); // eslint-disable-line

  useEffect(() => {
    applyFilters();
  }, [deductions, searchTerm, selectedType, selectedEmployee, showRecurrentOnly]); // eslint-disable-line

  useEffect(() => {
    if (deductions.length >= 0) calculateLocalSummary();
  }, [deductions]); // eslint-disable-line

  const loadDeductions = async () => {
    try {
      setLoading(true);
      const data = await getOtherDeductions(currentCompany.id);
      setDeductions(data);
    } catch (error) {
      console.error('Error loading deductions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateLocalSummary = () => {
    const totalCount       = deductions.length;
    const totalAmount      = deductions.reduce((s, d) => s + (parseFloat(d.monto) || 0), 0);
    const recurrentCount   = deductions.filter(d => d.recurrente).length;
    const affectedEmployees = new Set(deductions.map(d => d.employee_id)).size;
    setSummary({ total_count: totalCount, total_amount: totalAmount, recurrent_count: recurrentCount, affected_employees: affectedEmployees });
  };

  const applyFilters = () => {
    let filtered = [...deductions];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d =>
        d.concepto.toLowerCase().includes(term) ||
        d.employee_name?.toLowerCase().includes(term) ||
        d.tipo.toLowerCase().includes(term)
      );
    }
    if (selectedType !== 'all')     filtered = filtered.filter(d => d.tipo === selectedType);
    if (selectedEmployee !== 'all') filtered = filtered.filter(d => d.employee_id === selectedEmployee);
    if (showRecurrentOnly)          filtered = filtered.filter(d => d.recurrente);
    setFilteredDeductions(filtered);
    setPage(1);
  };

  const handleCreate = () => { setEditingDeduction(null); setShowModal(true); };
  const handleEdit   = (d)  => { setEditingDeduction(d);  setShowModal(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta deducción?')) return;
    try {
      await deleteOtherDeduction(id);
      await loadDeductions();
    } catch (error) {
      console.error('Error deleting deduction:', error);
      alert('Error al eliminar deducción');
    }
  };

  const handleSaveSuccess = () => {
    setShowModal(false);
    loadDeductions();
  };

  const getTypeInfo = (tipo) =>
    deductionTypes.find(t => t.value === tipo) || deductionTypes[deductionTypes.length - 1];

  const exportToExcel = () => alert('Función de exportación en desarrollo');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando deducciones...</p>
        </div>
      </div>
    );
  }

  const totalFiltrado = filteredDeductions.reduce((s, d) => s + parseFloat(d.monto || 0), 0);
  const totalPagesDeductions = Math.ceil(filteredDeductions.length / PAGE_SIZE);
  const pagedDeductions = filteredDeductions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-4">

      {/* ── Toolbar ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">

        {/* Left: pill + filters */}
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium flex-shrink-0">
          <span className="font-semibold text-gray-700">{filteredDeductions.length}</span> deducción{filteredDeductions.length !== 1 ? 'es' : ''}
        </span>

        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
          />
        </div>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 bg-white"
        >
          <option value="all">Todos los tipos</option>
          {deductionTypes.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <select
          value={selectedEmployee}
          onChange={(e) => setSelectedEmployee(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 bg-white"
        >
          <option value="all">Todos los empleados</option>
          {employees.filter(e => e.estado === 'activo').map(emp => (
            <option key={emp.id} value={emp.id}>{emp.nombre} {emp.apellido}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            checked={showRecurrentOnly}
            onChange={(e) => setShowRecurrentOnly(e.target.checked)}
            className="rounded"
          />
          Solo recurrentes
        </label>

        {/* Right: actions */}
        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <button
            onClick={exportToExcel}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download size={14} />
            Exportar
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={15} />
            Nueva Deducción
          </button>
        </div>
      </div>

      {/* ── Stat Cards ───────────────────────────────────────────── */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={MinusCircle} iconCls="text-blue-600" bgCls="bg-blue-50"
            label="Total Deducciones" value={summary.total_count}
          />
          <StatCard
            icon={DollarSign} iconCls="text-red-600" bgCls="bg-red-50"
            label="Monto Total"
            value={<span className="text-red-600">RD$ {fmt(summary.total_amount)}</span>}
          />
          <StatCard
            icon={Calendar} iconCls="text-blue-600" bgCls="bg-blue-50"
            label="Recurrentes" value={summary.recurrent_count}
          />
          <StatCard
            icon={Shield} iconCls="text-green-600" bgCls="bg-green-50"
            label="Empleados Afectados" value={summary.affected_employees}
          />
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Empleado</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Concepto</th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider">Monto</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Recurrente</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Cuotas</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredDeductions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center">
                    <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                    <p className="text-sm font-medium text-gray-500">No hay deducciones registradas</p>
                    <p className="text-xs text-gray-400 mt-1">Crea una nueva deducción para comenzar</p>
                  </td>
                </tr>
              ) : (
                pagedDeductions.map((deduction) => {
                  const typeInfo = getTypeInfo(deduction.tipo);
                  return (
                    <tr key={deduction.id} className="hover:bg-gray-50 transition-colors">

                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-gray-900">{deduction.employee_name}</p>
                        <p className="text-xs text-gray-400">{deduction.employee_id_display}</p>
                      </td>

                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${typeInfo.cls}`}>
                          {typeInfo.label}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{deduction.concepto}</p>
                        {deduction.categoria && (
                          <p className="text-xs text-gray-400 mt-0.5">{deduction.categoria}</p>
                        )}
                      </td>

                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-bold text-red-600">
                          RD$ {fmt(deduction.monto)}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        {deduction.recurrente ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Sí
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Una vez</span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-center">
                        {deduction.numero_cuotas ? (
                          <span className="text-sm font-semibold text-gray-700">
                            {deduction.cuota_actual}/{deduction.numero_cuotas}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(deduction)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(deduction.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredDeductions.length > 0 && (
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td colSpan="3" className="px-6 py-3 text-xs text-gray-500">
                    <span className="font-semibold text-gray-700">{filteredDeductions.length}</span>{' '}
                    deducción{filteredDeductions.length !== 1 ? 'es' : ''} mostrada{filteredDeductions.length !== 1 ? 's' : ''}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="text-sm font-bold text-red-600">RD$ {fmt(totalFiltrado)}</span>
                  </td>
                  <td colSpan="3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <Pagination
          page={page}
          totalPages={totalPagesDeductions}
          total={filteredDeductions.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      {/* ── Modal ────────────────────────────────────────────────── */}
      {showModal && (
        <OtherDeductionModal
          deduction={editingDeduction}
          employees={employees}
          deductionTypes={deductionTypes}
          onClose={() => setShowModal(false)}
          onSuccess={handleSaveSuccess}
        />
      )}
    </div>
  );
}
